/*  RichTextEditor — thin Tiptap wrapper for the Course Builder.
    ────────────────────────────────────────────────────────────
    Keeps the toolbar minimal (bold / italic / heading / list / quote
    + Embed) so the author focuses on content, not chrome. Emits HTML.

    Usage:
        <RichTextEditor value={html} onChange={setHtml} placeholder="…" />
*/
import React, { useEffect, useState } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
    TextB, TextItalic, TextHTwo, TextHThree,
    ListBullets, ListNumbers, Quotes, ArrowUUpLeft, ArrowUUpRight,
    Code,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { EmbedNode, encodeEmbed, decodeEmbed } from "@/components/creator/EmbedNode";


const TOOLBAR = [
    { key: "bold",         Icon: TextB,          label: "Bold",         run: (e) => e.chain().focus().toggleBold().run(),        active: (e) => e.isActive("bold") },
    { key: "italic",       Icon: TextItalic,     label: "Italic",       run: (e) => e.chain().focus().toggleItalic().run(),      active: (e) => e.isActive("italic") },
    { key: "h2",           Icon: TextHTwo,       label: "Heading 2",    run: (e) => e.chain().focus().toggleHeading({ level: 2 }).run(), active: (e) => e.isActive("heading", { level: 2 }) },
    { key: "h3",           Icon: TextHThree,     label: "Heading 3",    run: (e) => e.chain().focus().toggleHeading({ level: 3 }).run(), active: (e) => e.isActive("heading", { level: 3 }) },
    { key: "bullet",       Icon: ListBullets,    label: "Bulleted list", run: (e) => e.chain().focus().toggleBulletList().run(),  active: (e) => e.isActive("bulletList") },
    { key: "ordered",      Icon: ListNumbers,    label: "Numbered list", run: (e) => e.chain().focus().toggleOrderedList().run(), active: (e) => e.isActive("orderedList") },
    { key: "blockquote",   Icon: Quotes,         label: "Quote",        run: (e) => e.chain().focus().toggleBlockquote().run(),  active: (e) => e.isActive("blockquote") },
];


export default function RichTextEditor({ value, onChange, placeholder, disabled, size = "default" }) {
    const [embedOpen, setEmbedOpen] = useState(false);
    const [embedSeed, setEmbedSeed] = useState("");

    // Compact variant is used inside quiz / exam / rationale / short
    // instruction fields so 10-question exams don't turn into a
    // scrollathon. Full-size stays default for text lesson bodies.
    const minH = size === "sm" ? "min-h-[120px]" : "min-h-[180px]";

    const editor = useEditor({
        extensions: [StarterKit, EmbedNode],
        content: value || "",
        editable: !disabled,
        onUpdate: ({ editor: e }) => onChange?.(e.getHTML()),
        editorProps: {
            attributes: {
                class: `prose prose-sm max-w-none ${minH} px-3 py-2 focus:outline-none`,
                "data-testid": "richtext-editor-body",
            },
        },
    });

    // Keep editor in sync when parent value changes (e.g., load from server)
    useEffect(() => {
        if (!editor) return;
        if (editor.getHTML() !== (value || "")) {
            editor.commands.setContent(value || "", false);
        }
    }, [value, editor]);

    useEffect(() => {
        if (editor) editor.setEditable(!disabled);
    }, [disabled, editor]);

    const openEmbedModal = () => {
        if (!editor) return;
        // Prefill from currently-selected embed node (edit mode) if any.
        if (editor.isActive("embed")) {
            const attrs = editor.getAttributes("embed");
            setEmbedSeed(decodeEmbed(attrs.html) || "");
        } else {
            setEmbedSeed("");
        }
        setEmbedOpen(true);
    };

    const insertEmbed = (rawHtml) => {
        if (!editor) return;
        const html = (rawHtml || "").trim();
        if (!html) return;
        editor.chain().focus().insertContent({
            type: "embed",
            attrs: { html: encodeEmbed(html) },
        }).run();
        setEmbedOpen(false);
    };

    return (
        <div className="rounded-lg border border-input bg-background overflow-hidden focus-within:border-primary transition-colors" data-testid="richtext-editor">
            {!disabled && (
                <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-border bg-secondary/30" data-testid="richtext-toolbar">
                    {TOOLBAR.map(({ key, Icon, label, run, active }) => {
                        const isActive = editor && active(editor);
                        return (
                            <button
                                key={key}
                                type="button"
                                title={label}
                                onClick={() => run(editor)}
                                className={`w-7 h-7 grid place-items-center rounded transition-colors ${
                                    isActive
                                        ? "bg-primary/15 text-primary"
                                        : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                                }`}
                                data-testid={`richtext-${key}`}
                            >
                                <Icon size={12} weight={isActive ? "fill" : "regular"} />
                            </button>
                        );
                    })}
                    <div className="w-px h-4 bg-border mx-1" />
                    <button
                        type="button"
                        title="Embed / Custom HTML"
                        onClick={openEmbedModal}
                        className={`h-7 px-2 flex items-center gap-1 rounded transition-colors text-[10px] font-bold uppercase tracking-wider ${
                            editor?.isActive("embed")
                                ? "bg-primary/15 text-primary"
                                : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                        }`}
                        data-testid="richtext-embed"
                    >
                        <Code size={12} weight={editor?.isActive("embed") ? "fill" : "regular"} />
                        Embed
                    </button>
                    <div className="w-px h-4 bg-border mx-1" />
                    <button
                        type="button"
                        title="Undo"
                        onClick={() => editor?.chain().focus().undo().run()}
                        className="w-7 h-7 grid place-items-center rounded text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
                        data-testid="richtext-undo"
                    >
                        <ArrowUUpLeft size={12} />
                    </button>
                    <button
                        type="button"
                        title="Redo"
                        onClick={() => editor?.chain().focus().redo().run()}
                        className="w-7 h-7 grid place-items-center rounded text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
                        data-testid="richtext-redo"
                    >
                        <ArrowUUpRight size={12} />
                    </button>
                </div>
            )}
            <EditorContent editor={editor} />
            {editor && (
                <div className="px-3 py-1 border-t border-border bg-secondary/20 text-[10px] text-muted-foreground flex justify-between">
                    <span>{editor.storage.characterCount ? "" : ""}</span>
                    <span>{editor.getText().length} chars</span>
                </div>
            )}
            {!editor && placeholder && (
                <div className="p-3 text-sm text-muted-foreground">{placeholder}</div>
            )}

            {embedOpen && (
                <EmbedModal
                    initial={embedSeed}
                    onCancel={() => setEmbedOpen(false)}
                    onSave={insertEmbed}
                />
            )}
        </div>
    );
}


/* ─────────────────────────────────────────────────────────────
   Embed modal — collects an HTML snippet and inserts it as an
   Embed node. Deliberately unstyled inside a small overlay so we
   can preserve whatever site the author is embedding (YouTube,
   Vimeo, Loom, Twitter, CodePen, Google Maps, etc.).
   ───────────────────────────────────────────────────────────── */

function EmbedModal({ initial, onCancel, onSave }) {
    const [value, setValue] = useState(initial || "");

    return (
        <div className="fixed inset-0 z-[110] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="embed-modal" onClick={onCancel}>
            <div
                className="w-full max-w-2xl bg-card rounded-2xl shadow-2xl border border-border overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="px-5 py-4 border-b border-border flex items-center gap-2">
                    <Code size={18} weight="duotone" className="text-primary" />
                    <div>
                        <h3 className="font-display font-bold">Embed / Custom HTML</h3>
                        <p className="text-xs text-muted-foreground">
                            Paste an iframe embed (YouTube, Vimeo, Loom, Google Maps, CodePen, Twitter, etc.) or any custom HTML snippet.
                        </p>
                    </div>
                </div>
                <div className="p-5 space-y-3">
                    <Textarea
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        rows={9}
                        autoFocus
                        placeholder='e.g. <iframe src="https://www.youtube.com/embed/dQw4w9WgXcQ" width="560" height="315" frameborder="0" allowfullscreen></iframe>'
                        className="font-mono text-[12px]"
                        data-testid="embed-modal-textarea"
                    />
                    <div className="text-[11px] text-muted-foreground bg-secondary/40 rounded-md px-3 py-2">
                        <strong>Tip:</strong> On YouTube click <em>Share → Embed</em> to get the &lt;iframe&gt; code. On Vimeo use <em>Share → Embed</em>. Everything you paste renders exactly as-is on the learner side.
                    </div>
                </div>
                <div className="px-5 py-3 border-t border-border bg-secondary/20 flex items-center justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={onCancel} data-testid="embed-modal-cancel">Cancel</Button>
                    <Button
                        size="sm"
                        onClick={() => onSave(value)}
                        disabled={!(value || "").trim()}
                        data-testid="embed-modal-save"
                    >
                        Insert embed
                    </Button>
                </div>
            </div>
        </div>
    );
}
